import { useId, useState, type FormEvent } from 'react';

import {
  ACCOUNT_TYPES,
  CAPTURE_MODES,
  USAGE_SCOPES,
  isLiabilityAccountType,
  validateAccountInput,
  type AccountType,
  type CaptureMode,
  type UsageScope,
  type ValidationIssue,
} from '@pfo/domain';

import { todayInputValue } from '../../../shared/lib/dates.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { VALIDATION_MESSAGES } from '../../../shared/lib/validationMessages.js';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import { createAccount, type AccountContext } from '../data/accountsRepository.js';
import {
  ACCOUNT_TYPE_LABELS,
  CAPTURE_MODE_LABELS,
  USAGE_SCOPE_LABELS,
} from '../model/accountLabels.js';
import styles from '../../../shared/styles/forms.module.css';

/**
 * Adds an account. The default path is three fields — name, kind, balance — and everything
 * else sits behind "Chi tiết" (skill 13.1). The balance is not a column on the account: it
 * becomes an opening journal entry (DBD-006), which is why it can only be entered here or
 * on the account screen, never edited as a value.
 */
export function AccountForm({
  context,
  timeZone,
  onCreated,
  onCancel,
}: {
  context: AccountContext;
  /** Household timezone — decides what "today" means in the date field. */
  timeZone: string;
  onCreated: (result: { accountId: string; openingBalanceFailed: boolean }) => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const typeId = useId();
  const balanceId = useId();
  const dateId = useId();
  const institutionId = useId();
  const scopeId = useId();
  const captureId = useId();
  const errorId = useId();

  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('bank');
  const [openingBalance, setOpeningBalance] = useState<number | null>(null);
  const [openingDate, setOpeningDate] = useState(() => todayInputValue(timeZone));
  const [institution, setInstitution] = useState('');
  const [usageScope, setUsageScope] = useState<UsageScope>('personal');
  const [captureMode, setCaptureMode] = useState<CaptureMode>('manual');

  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const isLiability = isLiabilityAccountType(accountType);
  const issueOf = (field: string) => issues.find((issue) => issue.field === field);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);

    const input = {
      name,
      accountType,
      usageScope,
      captureMode,
      ...(openingBalance === null ? {} : { openingBalance }),
    };

    const validation = validateAccountInput(input);
    if (!validation.ok) {
      setIssues(validation.issues);
      return;
    }
    setIssues([]);

    setSaving(true);
    try {
      // Confirmed write, never optimistic: this creates a row the ledger will point at
      // (skill 11.1).
      const result = await createAccount(context, {
        ...input,
        ...(institution.trim() === '' ? {} : { institution }),
        ...(openingDate === '' ? {} : { openingDate }),
      });
      onCreated(result);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
      <div className={styles.field}>
        <label htmlFor={nameId}>Tên tài khoản</label>
        <input
          id={nameId}
          className={styles.input}
          type="text"
          value={name}
          placeholder="Ví dụ: Tài khoản nhận lương"
          enterKeyHint="next"
          autoComplete="off"
          aria-invalid={issueOf('name') !== undefined}
          aria-describedby={issueOf('name') ? `${nameId}-error` : undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {issueOf('name') ? (
          <p className={styles.fieldError} id={`${nameId}-error`}>
            {VALIDATION_MESSAGES[issueOf('name')!.code]}
          </p>
        ) : null}
      </div>

      <div className={styles.field}>
        <label htmlFor={typeId}>Loại</label>
        <select
          id={typeId}
          className={styles.input}
          value={accountType}
          onChange={(event) => setAccountType(event.target.value as AccountType)}
        >
          {ACCOUNT_TYPES.map((type) => (
            <option key={type} value={type}>
              {ACCOUNT_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label htmlFor={balanceId}>{isLiability ? 'Số tiền còn nợ' : 'Số dư hiện tại'}</label>
        <MoneyInput
          id={balanceId}
          value={openingBalance}
          onChange={setOpeningBalance}
          describedBy={`${balanceId}-hint`}
        />
        <p className={styles.hint} id={`${balanceId}-hint`}>
          {isLiability
            ? 'Nhập số còn phải trả. Để trống nếu đã trả hết.'
            : 'Số dư thật lúc bắt đầu dùng ứng dụng. Để trống nếu bằng 0.'}
        </p>
        {issueOf('openingBalance') ? (
          <p className={styles.fieldError}>
            {VALIDATION_MESSAGES[issueOf('openingBalance')!.code]}
          </p>
        ) : null}
      </div>

      {openingBalance !== null && openingBalance !== 0 ? (
        <div className={styles.field}>
          <label htmlFor={dateId}>Số dư này tính đến ngày</label>
          <input
            id={dateId}
            className={styles.input}
            type="date"
            value={openingDate}
            onChange={(event) => setOpeningDate(event.target.value)}
          />
        </div>
      ) : null}

      <details className={styles.advanced}>
        <summary className={styles.summary}>Chi tiết</summary>

        <div className={styles.field}>
          <label htmlFor={institutionId}>Ngân hàng / nhà cung cấp</label>
          <input
            id={institutionId}
            className={styles.input}
            type="text"
            value={institution}
            autoComplete="off"
            onChange={(event) => setInstitution(event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor={scopeId}>Dùng cho</label>
          <select
            id={scopeId}
            className={styles.input}
            value={usageScope}
            onChange={(event) => setUsageScope(event.target.value as UsageScope)}
          >
            {USAGE_SCOPES.map((scope) => (
              <option key={scope} value={scope}>
                {USAGE_SCOPE_LABELS[scope]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor={captureId}>Cách ghi giao dịch</label>
          <select
            id={captureId}
            className={styles.input}
            value={captureMode}
            onChange={(event) => setCaptureMode(event.target.value as CaptureMode)}
          >
            {CAPTURE_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {CAPTURE_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
          <p className={styles.hint}>
            Ví điện tử như MoMo chọn “Nhập tổng cuối tháng” để đối chiếu một lần mỗi tháng.
          </p>
        </div>
      </details>

      {message ? (
        <p className={styles.error} id={errorId} role="alert">
          {message}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button type="submit" className={styles.primary} disabled={isSaving}>
          {isSaving ? 'Đang lưu…' : 'Lưu tài khoản'}
        </button>
      </div>
    </form>
  );
}
